import { Plus } from "lucide-react";
import t from "@/lang/lang";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import { Separator } from "@/shared/components/ui/separator";
import { cn } from "@/shared/lib/utils";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellDecimalInput,
  ETableCellInput,
  ETableCellSelect,
  ETableHead,
  ETableHeader,
  ETableOptions,
  ETableRow,
} from "@/shared/components/tables/e-table";
import {
  addRow,
  duplicateRow,
  GesecInvoice,
  GesecInvoiceRow,
  GesecInvoiceRowKind,
  GesecInvoiceRowKindListSelectItems,
  newGesecInvoiceRow,
  updateRows,
} from "../types/gesec-invoice";
import { BusinessCentralGesecCustomerSelect } from "./business-central-gesec-customer-select";
import { useBusinessCentralMatchOneToOneShowQuery } from "@/services/backend/business-central/api/match-one-to-one-api";
import { MatchType } from "../types/match-type";
import { TextInputField } from "@/shared/components/field/input-field";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import {
  selectItemsWithValue,
  useBusinessCentralGesecResourceSelectItems,
} from "./business-central-gesec-resource-select";

export function GesecInvoiceFields({
  invoice,
  onInvoiceChange,
  disabled,
}: {
  invoice: GesecInvoice;
  onInvoiceChange: (invoice: GesecInvoice) => void;
  disabled: boolean;
}) {
  const { companyId: activeCompanyId } = useActiveCompany();
  const { data: match, error } = useBusinessCentralMatchOneToOneShowQuery({
    type: MatchType.CompanyToCompany,
    id: activeCompanyId,
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Rechnungsdaten")}</CardTitle>
        <CardDescription>
          {t(
            "Diese Daten werden an Business Central übermittelt. Die gesperrten Felder werden automatisch ausgefüllt. Sie können hier den Debitor, die Vorgangsnummer und Positionen anpassen. Diese Daten werden nicht weiter überprüft. Ihre Änderungen werden ohne Prüfung angenommen.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4">
          <div className="grid gap-4 md:grid-cols-3">
            <TextInputField
              label={t("Vorgangsnummer (GSM)")}
              text={invoice.processNumber}
              onTextChange={(processNumber) =>
                onInvoiceChange({ ...invoice, processNumber })
              }
              errors={[]}
              disabled={disabled}
            />
            <Field>
              <FieldLabel>{t("Business Central Debitor")}</FieldLabel>
              <BusinessCentralGesecCustomerSelect
                companyId={match?.bcId ?? ""}
                value={invoice.debitorNumber}
                onValueChange={(debitorNumber) =>
                  onInvoiceChange({ ...invoice, debitorNumber })
                }
                disabled={!match || disabled}
              />
            </Field>
            <TextInputField
              label={t("Business Central ID")}
              text={invoice.id}
              onTextChange={() => null}
              errors={[]}
              disabled
            />
            <TextInputField
              label={t("Business Central Verarbeitungsstatus")}
              text={invoice.processStatus}
              onTextChange={() => null}
              errors={[]}
              disabled
            />
            <TextInputField
              label={t("Rechnungsnummer")}
              text={invoice.invoiceNo}
              onTextChange={() => null}
              errors={[]}
              disabled
            />
            <TextInputField
              label={t("Rechnungsnummer")}
              text={invoice.postSalesInvoiceNo}
              onTextChange={() => null}
              errors={[]}
              disabled
            />
          </div>
          <Separator />
          <GesecInvoiceRows
            invoice={invoice}
            onInvoiceChange={onInvoiceChange}
            bcCompanyId={match?.bcId ?? ""}
            disabled={disabled}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function GesecInvoiceRows({
  invoice,
  onInvoiceChange,
  bcCompanyId,
  disabled,
}: {
  invoice: GesecInvoice;
  onInvoiceChange: (invoice: GesecInvoice) => void;
  bcCompanyId: string;
  disabled: boolean;
}) {
  const resourceItems = useBusinessCentralGesecResourceSelectItems({
    companyId: bcCompanyId,
  });

  const updateRow = (row: GesecInvoiceRow) => {
    onInvoiceChange({
      ...invoice,
      rows: invoice.rows.map((r) => (r.id === row.id ? row : r)),
    });
  };

  return (
    <ETable>
      <ETableHeader>
        <ETableRow>
          <ETableHead>{t("Pos. Nr.")}</ETableHead>
          <ETableHead>{t("Art")}</ETableHead>
          <ETableHead>{t("Beschreibung")}</ETableHead>
          <ETableHead>{t("Beschreibung2")}</ETableHead>
          <ETableHead>{t("Ressourcen Nr.")}</ETableHead>
          <ETableHead>{t("Menge")}</ETableHead>
          <ETableHead>{t("Einheit")}</ETableHead>
          <ETableHead>{t("Stück Preis")}</ETableHead>
          <ETableHead>{t("Zeilenbetrag")}</ETableHead>
          <ETableHead className="w-10" />
        </ETableRow>
      </ETableHeader>
      <ETableBody>
        {invoice.rows.length === 0 && (
          <ETableRow>
            <ETableCell colSpan={10} className={cn("!py-2 text-center")}>
              <div className="flex items-center justify-center gap-2">
                <span>
                  {t(
                    "Es sind noch keine Rechnungspositionen hinzugefügt worden.",
                  )}
                </span>
                <Button
                  onClick={() => onInvoiceChange(addRow(invoice))}
                  variant="secondary"
                  size="sm"
                  disabled={disabled}
                >
                  <Plus />
                </Button>
              </div>
            </ETableCell>
          </ETableRow>
        )}
        {invoice.rows.map((row, index) => (
          <ETableRow key={row.id}>
            <ETableCellInput
              value={row.positionNo}
              onChange={(e) =>
                updateRow({ ...row, positionNo: e.target.value })
              }
              disabled={disabled}
            />
            <ETableCellSelect
              value={row.kind}
              onValueChange={(kind) =>
                updateRow({ ...row, kind: kind as GesecInvoiceRowKind })
              }
              disabled={disabled}
              label="Art"
              items={GesecInvoiceRowKindListSelectItems}
            />
            <ETableCellInput
              value={row.description}
              onChange={(e) =>
                updateRow({ ...row, description: e.target.value })
              }
              disabled={disabled}
            />
            <ETableCellInput
              value={row.description2}
              onChange={(e) =>
                updateRow({ ...row, description2: e.target.value })
              }
              disabled={disabled}
            />
            <ETableCellSelect
              value={row.resourceNo}
              onValueChange={(resourceNo) => updateRow({ ...row, resourceNo })}
              disabled={disabled || !bcCompanyId}
              label={t("Ressourcen Nr.")}
              placeholder={t("Ressource")}
              items={selectItemsWithValue(resourceItems, row.resourceNo)}
            />
            <ETableCellInput
              type="number"
              value={row.quantity}
              onChange={(e) =>
                updateRow({ ...row, quantity: Number(e.target.value) })
              }
              disabled={disabled}
            />
            <ETableCellInput
              value={row.unit}
              onChange={(e) => updateRow({ ...row, unit: e.target.value })}
              disabled={disabled}
            />
            <ETableCellDecimalInput
              value={row.unitPrice}
              onValueChange={(unitPrice) => updateRow({ ...row, unitPrice })}
              disabled={disabled}
            />
            <ETableCellDecimalInput
              value={row.lineAmount}
              onValueChange={(lineAmount) => updateRow({ ...row, lineAmount })}
              disabled={disabled}
            />
            <ETableCell className="px-0">
              <ETableOptions
                item={row}
                rowIndex={index}
                items={invoice.rows}
                onItemsChange={(rows) => {
                  onInvoiceChange(updateRows(invoice, rows));
                }}
                newItem={() => newGesecInvoiceRow(invoice)}
                duplicate={duplicateRow}
              />
            </ETableCell>
          </ETableRow>
        ))}
      </ETableBody>
    </ETable>
  );
}
